import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Chip,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  Gavel as GavelIcon,
  MenuBook as BookIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  ChatBubbleOutline as ChatIcon,
  Scale as ScaleIcon,
  Handshake as HandshakeIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CaseApi from "Service/Case.api";
import Loader from "Components/Loader";
import { UserInitialStateType } from "DataTypes/Redux";

const DisputeDirectory: React.FC = () => {
  const navigate = useNavigate();
  const { myInfo }: UserInitialStateType = useSelector((state: any) => state?.user);
  const [loading, setLoading] = useState(true);
  const [disputes, setDisputes] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const isClaimant =
    myInfo?.userType === "USER" ||
    myInfo?.userType === "CORPORATEUSER" ||
    myInfo?.userType === "NRIUSER";

  useEffect(() => {
    if (myInfo?.id) {
      loadDisputes();
    }
  }, [myInfo]);

  const loadDisputes = async () => {
    setLoading(true);
    try {
      const res = await CaseApi.getCaseListByUserId({
        clientId: myInfo.id,
        pageSize: 100,
      });
      if (res?.success && Array.isArray(res?.data?.data)) {
        // Filter only ODR cases
        const odrList = res.data.data.filter(
          (c: any) => {
            const name = (c.orderTypeName || c.caseTypeName || c.orderType || "").toLowerCase();
            return name.includes("mediation") || name.includes("arbitration") || name.includes("negotiation") || (c.odrType && c.odrType.length > 0);
          }
        );
        setDisputes(odrList);
      }
    } catch (err) {
      console.error("Failed to load disputes", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDisputes = disputes.filter(
    (d: any) =>
      d.pkCase.toString().includes(searchQuery) ||
      (d.caseTypeName && d.caseTypeName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (d.clientFullName && d.clientFullName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (d.odrStatus && d.odrStatus.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 }, minHeight: "100%", bgcolor: "#f8fafc" }}>
      {/* Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "24px",
          background: "linear-gradient(135deg, #283165 0%, #1c234b 100%)",
          color: "white",
          boxShadow: "0 10px 30px rgba(28, 35, 75, 0.15)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" gap={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: "Outfit", mb: 1, color: "white" }}>
              ODR Platform
            </Typography>
            <Typography variant="body1" sx={{ color: "#cbd5e1", maxWidth: "600px" }}>
              Access professional Online Dispute Resolution services. File a new mediation, arbitration, or negotiation case, and track existing resolutions in real-time.
            </Typography>
          </Box>
          <GavelIcon sx={{ fontSize: 80, color: "rgba(255, 255, 255, 0.1)", display: { xs: "none", md: "block" } }} />
        </Stack>
      </Paper>

      {/* Case Creation Cards (Only for Claimants) */}
      {isClaimant && (
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "Outfit", color: "#1e293b", mb: 3 }}>
            File a New Case
          </Typography>
          <Grid container spacing={3}>
            {/* Mediation Card */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "20px",
                  border: "1px solid #e2e8f0",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 24px rgba(40, 49, 101, 0.08)",
                    borderColor: "#283165",
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1.5 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: "12px",
                        bgcolor: "#ecfdf5",
                        color: "#10b981",
                        display: "flex",
                      }}
                    >
                      <HandshakeIcon />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "Outfit", color: "#1e293b" }}>
                      Online Mediation
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 3, height: "40px" }}>
                    Resolve your dispute quickly and amicably using an expert mediator to facilitate a mutual settlement.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      const isCorporate =
                        myInfo?.userType?.toLowerCase() === "corporate_user" ||
                        myInfo?.userType?.toLowerCase() === "corporateuser" ||
                        myInfo?.userType?.toLowerCase() === "corporate";
                      if (isCorporate) {
                        navigate("/case/case-creation", { state: { preselectOrderType: 1 } });
                      } else {
                        navigate("/case/case-creation/odr?type=mediation&orderTypeId=1");
                      }
                    }}
                    sx={{
                      bgcolor: "#283165",
                      borderRadius: "12px",
                      py: 1.2,
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": { bgcolor: "#1c234b" },
                    }}
                  >
                    File Mediation
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Arbitration Card */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "20px",
                  border: "1px solid #e2e8f0",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 24px rgba(40, 49, 101, 0.08)",
                    borderColor: "#283165",
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1.5 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: "12px",
                        bgcolor: "#eff6ff",
                        color: "#3b82f6",
                        display: "flex",
                      }}
                    >
                      <ScaleIcon />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "Outfit", color: "#1e293b" }}>
                      Online Arbitration
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 3, height: "40px" }}>
                    Obtain a legally binding decision from an experienced arbitrator to resolve complex disputes.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      const isCorporate =
                        myInfo?.userType?.toLowerCase() === "corporate_user" ||
                        myInfo?.userType?.toLowerCase() === "corporateuser" ||
                        myInfo?.userType?.toLowerCase() === "corporate";
                      if (isCorporate) {
                        navigate("/case/case-creation", { state: { preselectOrderType: 2 } });
                      } else {
                        navigate("/case/case-creation/odr?type=arbitration&orderTypeId=2");
                      }
                    }}
                    sx={{
                      bgcolor: "#283165",
                      borderRadius: "12px",
                      py: 1.2,
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": { bgcolor: "#1c234b" },
                    }}
                  >
                    File Arbitration
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Negotiation Card */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "20px",
                  border: "1px solid #e2e8f0",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 24px rgba(40, 49, 101, 0.08)",
                    borderColor: "#283165",
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1.5 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: "12px",
                        bgcolor: "#faf5ff",
                        color: "#a855f7",
                        display: "flex",
                      }}
                    >
                      <ChatIcon />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "Outfit", color: "#1e293b" }}>
                      Online Negotiation
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 3, height: "40px" }}>
                    Initiate direct, structured negotiation dialogues with the opposing party to arrive at a settlement.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      const isCorporate =
                        myInfo?.userType?.toLowerCase() === "corporate_user" ||
                        myInfo?.userType?.toLowerCase() === "corporateuser" ||
                        myInfo?.userType?.toLowerCase() === "corporate";
                      if (isCorporate) {
                        navigate("/case/case-creation", { state: { preselectOrderType: 56 } });
                      } else {
                        navigate("/case/case-creation/odr?type=negotiation&orderTypeId=56");
                      }
                    }}
                    sx={{
                      bgcolor: "#283165",
                      borderRadius: "12px",
                      py: 1.2,
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": { bgcolor: "#1c234b" },
                    }}
                  >
                    File Negotiation
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ODR Dispute Directory Case Listing */}
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "Outfit", color: "#1e293b", mb: 3 }}>
          ODR Dispute Directory
        </Typography>

        {/* Search Bar */}
        <Box sx={{ mb: 4, display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by Case ID, Type, or Claimant Name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#64748b" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                bgcolor: "white",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": { borderColor: "#283165" },
              },
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <Loader />
          </Box>
        ) : filteredDisputes.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 8,
              textAlign: "center",
              borderRadius: "24px",
              border: "1px dashed #cbd5e1",
              bgcolor: "transparent",
            }}
          >
            <BookIcon sx={{ fontSize: 60, color: "#94a3b8", mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
              No Active ODR Disputes Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Any disputes submitted or notices received under ODR will appear here.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredDisputes.map((dispute: any) => {
              const isArbitration =
                dispute.orderTypeName === "Online Arbitration" || dispute.orderTypeId === 55;
              const isNegotiation =
                dispute.orderTypeName === "Online Negotiation" || dispute.orderTypeId === 56;

              return (
                <Grid size={{ xs: 12 }} key={dispute.pkCase}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: "20px",
                      border: "1px solid #e2e8f0",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 20px -5px rgba(0, 0, 0, 0.05)",
                        borderColor: "#283165",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Grid container spacing={3} alignItems="center">
                        <Grid size={{ xs: 12, md: 8 }}>
                          <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Chip
                                label={dispute.orderTypeName || (isArbitration ? "Online Arbitration" : isNegotiation ? "Online Negotiation" : "Online Mediation")}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  bgcolor: isArbitration
                                    ? "#e0f2fe"
                                    : isNegotiation
                                    ? "#faf5ff"
                                    : "#ecfdf5",
                                  color: isArbitration
                                    ? "#0369a1"
                                    : isNegotiation
                                    ? "#a855f7"
                                    : "#047857",
                                }}
                              />
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 700, fontFamily: "Outfit", color: "#1e293b" }}
                              >
                                Active ODR Case Room: #{dispute.pkCase} ({dispute.caseTypeName})
                              </Typography>
                            </Stack>

                            <Typography variant="body2" sx={{ color: "#64748b" }}>
                              Claimant: <strong>{dispute.clientFullName}</strong> | Status:{" "}
                              <Chip
                                label={dispute.odrCustomStatus || dispute.odrStatus || dispute.currentProcessingStatusName || "INITIATED"}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{
                                  height: 22,
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  fontFamily: "Outfit",
                                }}
                              />
                            </Typography>

                            <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <CalendarIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                                <Typography variant="caption" sx={{ color: "#64748b" }}>
                                  Filed: {new Date(dispute.dateCreated).toLocaleDateString()}
                                </Typography>
                              </Stack>
                              {dispute.claimAmount > 0 && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <MoneyIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                                    Claim Value: ₹{dispute.claimAmount.toLocaleString("en-IN")}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                          </Stack>
                        </Grid>

                        <Grid
                          size={{ xs: 12, md: 4 }}
                          sx={{
                            display: "flex",
                            justifyContent: { xs: "flex-start", md: "flex-end" },
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => navigate(`/odr-workspace/${dispute.pkCase}`)}
                            sx={{
                              bgcolor: "#283165",
                              color: "white",
                              fontWeight: 700,
                              borderRadius: "12px",
                              textTransform: "none",
                              fontFamily: "Outfit",
                              px: 4,
                              py: 1.5,
                              "&:hover": { bgcolor: "#1c234b" },
                            }}
                          >
                            Open ODR Case Room
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default DisputeDirectory;
